import type { InputHTMLAttributes, KeyboardEvent, ReactNode } from 'react';

import { Icon, type IconName } from '../Icon';
import styles from './Input.module.css';

export type InputSize = 'large' | 'default' | 'small';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'prefix' | 'size' | 'value'> {
  ariaLabel: string;
  /** Ant Design `allowClear`：有值时悬停或聚焦显示清空按钮，点击后回传空串。 */
  allowClear?: boolean;
  /** Figma Error 状态：边框转为危险色，提示文案同步变红。 */
  error?: boolean;
  leadingIcon?: IconName;
  /** 清空按钮点击后的附加回调，用于重置与输入值联动的页面状态。 */
  onClear?: () => void;
  onValueChange: (value: string) => void;
  /** 回车提交：列表页筛选、搜索这类场景不用再在页面里自己判 Enter。 */
  onPressEnter?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Figma 前缀槽：任意节点（文字、图标、标签）。与 `leadingIcon` 可叠加，图标在前。 */
  prefix?: ReactNode;
  /** Figma Show-prompt：输入框下方的提示或错误文案。 */
  prompt?: ReactNode;
  size?: InputSize;
  /** Figma 后缀槽：任意节点。与 `allowClear` 同用时清空按钮排在后缀之前。 */
  suffix?: ReactNode;
  trailingIcon?: IconName;
  value: string;
}

export function Input({ allowClear = false, ariaLabel, className, error = false, leadingIcon, onClear, onKeyDown, onPressEnter, onValueChange, prefix, prompt, size = 'default', suffix, trailingIcon, value, ...inputProps }: InputProps) {
  const iconSize = size === 'small' ? 'xs' : 'sm';
  const clearable = allowClear && !inputProps.disabled && value !== '';

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(event);
    if (event.key === 'Enter') onPressEnter?.(event);
  }

  /* 没有提示文案时不再多包一层：调用方的 className 直接落在带边框的容器上，
     避免页面样式与组件边框叠加成双层边框。 */
  const box = (
    <span className={[styles.box, className].filter(Boolean).join(' ')} data-clearable={clearable || undefined} data-error={error || undefined} data-size={size}>
      {leadingIcon ? <Icon className={styles.icon} name={leadingIcon} size={iconSize} /> : null}
      {prefix ? <span className={styles.slot}>{prefix}</span> : null}
      <input aria-label={ariaLabel} className={styles.control} onChange={(event) => onValueChange(event.target.value)} onKeyDown={handleKeyDown} value={value} {...inputProps} />
      {clearable
        /* 清空按钮常驻 DOM 只在悬停／聚焦时显形，避免出现时把右侧内容顶开。 */
        ? <button aria-label={`清空${ariaLabel}`} className={styles.clear} onClick={() => { onValueChange(''); onClear?.(); }} type="button">
          <Icon name="circle-close" size={iconSize} />
        </button>
        : null}
      {suffix ? <span className={styles.slot}>{suffix}</span> : null}
      {trailingIcon ? <Icon className={styles.icon} name={trailingIcon} size={iconSize} /> : null}
    </span>
  );

  if (!prompt) return box;

  return <span className={styles.root}>{box}<span className={styles.prompt}>{prompt}</span></span>;
}
